/**
 * Renders a notification's TITLE in the current UI language.
 *
 * The backend composes it as `"<English lead-in>: <meeting title>"` — see
 * `postgres_ingest._notify_recording_failed` ("Recording failed" / "Recording
 * stopped") and `_notify_summary_ready` ("Summary ready"). The `body` half of
 * these notifications is already translated client-side, by replacing a stable
 * code with a fixed key; the title was left as stored, so a Hindi drawer
 * rendered an English heading in bold above a Hindi sentence — the larger,
 * more legible of the two lines.
 *
 * WHY THE LEAD-IN IS DERIVED FROM `kind` AND THE CODE, NOT MATCHED AS PROSE.
 * The expected English is reconstructed from what the row already tells us
 * (`kind`, plus `recordingFailureCode(body)` for the two failure variants) and
 * is stripped only when the stored title actually starts with it. So this is
 * never a search for English words in user data: the meeting title is whatever
 * remains, is never translated, and a title that does not carry the expected
 * prefix — an older format, a lead-in somebody rewords later — is returned
 * UNCHANGED rather than mangled. The failure direction is "still shows the
 * correct English", which is what the stored value was anyway.
 *
 * The alternative was storing a bare meeting title and composing entirely on
 * the client. That reads cleaner and is worse here: the stored title is the
 * only self-describing copy for anything that is not this SPA (an ops query, a
 * digest), and it would need a third format migration on a table this change
 * is already migrating twice.
 */

import { RECORDING_PARTIAL_CODES, recordingFailureCode } from './notificationFailure'

type Translate = (key: string, vars?: Record<string, string | number>) => string

/** What the backend puts in front of the meeting title, per notification. */
const LEAD_INS: Record<string, { english: string; key: string }> = {
  'recording_failed:failed': {
    english: 'Recording failed',
    key: 'notifications.leadIn.recordingFailed',
  },
  'recording_failed:partial': {
    english: 'Recording stopped',
    key: 'notifications.leadIn.recordingStopped',
  },
  summary_ready: {
    english: 'Summary ready',
    key: 'notifications.leadIn.summaryReady',
  },
}

function leadInFor(kind: string, body: string) {
  if (kind !== 'recording_failed') return LEAD_INS[kind]
  // The same split `NotificationBell` uses for the tone: `removed_by_host` is
  // billable and not a failure, and the backend words its lead-in differently.
  return RECORDING_PARTIAL_CODES.has(recordingFailureCode(body))
    ? LEAD_INS['recording_failed:partial']
    : LEAD_INS['recording_failed:failed']
}

export function notificationTitle(
  item: { kind: string; title: string; body: string },
  t: Translate,
): string {
  const leadIn = leadInFor(item.kind, item.body)
  if (!leadIn) return item.title
  const prefix = `${leadIn.english}: `
  if (!item.title.startsWith(prefix)) return item.title
  // `slice`, not a split — a meeting legitimately called "Q3: revenue" keeps
  // its own colon.
  return `${t(leadIn.key)}: ${item.title.slice(prefix.length)}`
}
